import { useState } from "react";
import { Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useCouple } from "@/hooks/useCouple";
import { Money } from "@/components/shared/Money";
import { Bar, Eyebrow, Hint } from "@/components/shared/Primitives";
import { parseAmount } from "@/lib/format";

export const AllowanceCard = ({ allowance, compact = false }) => {
  const { couple, updateCouple, me } = useCouple();
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState("");
  if (!allowance) return null;
  const entries = Object.entries(allowance);
  const configured = entries.some(([, a]) => a.limit > 0);

  const save = async (pid) => {
    await updateCouple.mutateAsync({ allowance: { ...(couple?.allowance || {}), [pid]: parseAmount(val) } });
    toast.success("Mesada combinada");
    setEditing(null);
  };

  if (compact) {
    const mine = allowance[me?.id];
    if (!mine || mine.limit <= 0) return null;
    return (
      <p className="text-xs text-[var(--coffee-muted)] mt-2" data-testid="allowance-compact">
        sua mesada: {mine.remaining >= 0 ? <>restam <Money value={mine.remaining} className="font-semibold text-[var(--coffee)]" testId="allowance-compact-remaining" /></> : <>passou <Money value={-mine.remaining} className="font-semibold text-[var(--coral)]" testId="allowance-compact-remaining" /> — ajustar ou ver depois?</>}
      </p>
    );
  }

  return (
    <section className="paper p-5 mb-4" data-testid="allowance-card">
      <Eyebrow>mesada do casal</Eyebrow>
      <p className="text-xs text-[var(--coffee-muted)] mt-1 mb-3">Um valor mensal de cada um pra gastar sem explicar. Conta o que for lançado como <strong>meu</strong> ou <strong>seu</strong>.</p>
      <div className="grid grid-cols-2 gap-2">
        {entries.map(([pid, a]) => (
          <div key={pid} className="bg-[var(--cream-surface)] rounded-2xl p-3" data-testid={`allowance-${pid}`}>
            <div className="flex items-center justify-between">
              <p className="eyebrow">{a.name}{pid === me?.id ? " (eu)" : ""}</p>
              {editing !== pid && <button className="w-8 h-8 -mr-2 flex items-center justify-center text-[var(--coffee-muted)]" onClick={() => { setEditing(pid); setVal(a.limit || ""); }} aria-label="definir mesada" data-testid={`allowance-edit-${pid}`}><Pencil size={12} /></button>}
            </div>
            {editing === pid ? (
              <div className="flex gap-1 mt-1">
                <input autoFocus inputMode="decimal" className="field h-9 px-2 text-sm num" value={val} onChange={(e) => setVal(e.target.value)} placeholder="0,00" data-testid={`allowance-input-${pid}`} />
                <button className="w-9 h-9 rounded-full bg-[var(--coffee)] text-[var(--cream)] flex items-center justify-center shrink-0" onClick={() => save(pid)} data-testid={`allowance-save-${pid}`}><Check size={14} /></button>
              </div>
            ) : a.limit > 0 ? (
              <>
                <p className="text-sm mt-1"><Money value={a.spent} className="font-bold" testId={`allowance-spent-${pid}`} /> <span className="text-[var(--coffee-muted)]">/ <Money value={a.limit} /></span></p>
                <Bar ratio={a.spent / a.limit} color="var(--coral)" className="mt-2" />
                <p className="text-[11px] mt-1 num" style={{ color: a.remaining < 0 ? "var(--coral)" : "var(--coffee-muted)" }}>{a.remaining >= 0 ? "restam " : "passou "}<Money value={Math.abs(a.remaining)} testId={`allowance-remaining-${pid}`} /></p>
              </>
            ) : (
              <p className="text-xs text-[var(--coffee-muted)] mt-1 italic">sem mesada definida</p>
            )}
          </div>
        ))}
      </div>
      {!configured && <Hint className="mt-3">Sugestão comum: 5–10% da renda de cada um. É combinado, não regra.</Hint>}
    </section>
  );
};
